import { create } from 'zustand';

import {
  getNotificationId,
  isNotificationRead,
  markNotificationRead,
  mergeNotificationReadState,
} from 'utils/notification';

/** Maximum number of notifications kept in memory across the drawer and notification page. */
const MAX_NOTIFICATIONS = 100;

/**
 * Resolves a stable string key for a notification or raw ID.
 * Returns `null` when no valid ID can be extracted.
 * @param {object|string|number} notificationOrId
 * @returns {string|null}
 */
const toNotificationKey = notificationOrId => {
  const notificationId = getNotificationId(notificationOrId);

  if (notificationId === null) return null;

  return String(notificationId);
};

/**
 * Global store for in-app notifications.
 * Shared between the notification drawer and the notification page.
 */
const useNotificationStore = create(set => ({
  /** @type {object[]} List of notification objects, newest first. */
  notifications: [],

  /** @type {number} Count of unread notifications. */
  unreadCount: 0,

  /**
   * Directly sets the unread count (e.g. from a server-side badge endpoint).
   * @param {number} unreadCount
   */
  setUnreadCount: unreadCount => {
    set({ unreadCount });
  },

  /**
   * Prepends a single notification if it does not already exist in the list.
   * @param {object} notification
   */
  addNotification: notification => {
    set(state => {
      const incomingKey = toNotificationKey(notification);
      const exists =
        incomingKey !== null && state.notifications.some(existing => toNotificationKey(existing) === incomingKey);

      if (exists) return state;

      const updated = [notification, ...state.notifications].slice(0, MAX_NOTIFICATIONS);

      return {
        notifications: updated,
        unreadCount: state.unreadCount + (isNotificationRead(notification) ? 0 : 1),
      };
    });
  },

  /**
   * Replaces the notification list with a fresh page-1 fetch result.
   * Preserves local read state for any notifications that already exist in the store.
   * Leaves `unreadCount` alone — the loaded slice is at most one page, so counting
   * it would cap the badge at the server page size. `setUnreadCount` owns that number.
   * @param {object[]} notifications
   */
  setNotifications: notifications => {
    set(state => {
      const existingNotifications = new Map();

      state.notifications.forEach(notification => {
        const key = toNotificationKey(notification);

        if (key !== null) {
          existingNotifications.set(key, notification);
        }
      });

      const mergedNotifications = notifications.map(notification =>
        mergeNotificationReadState(existingNotifications.get(toNotificationKey(notification)), notification)
      );

      return { notifications: mergedNotifications };
    });
  },

  /**
   * Appends notifications from subsequent pages, skipping duplicates.
   * Used by the notification page's "load more" flow.
   * @param {object[]} newNotifications
   */
  appendNotifications: newNotifications => {
    set(state => {
      const existingIds = new Set(state.notifications.map(toNotificationKey).filter(key => key !== null));
      const unique = newNotifications
        .filter(notification => {
          const key = toNotificationKey(notification);

          if (key === null) return true;

          return !existingIds.has(key);
        })
        .map(notification => {
          const notificationKey = toNotificationKey(notification);
          const currentNotification =
            notificationKey === null
              ? null
              : state.notifications.find(existing => toNotificationKey(existing) === notificationKey);

          return mergeNotificationReadState(currentNotification, notification);
        });
      const merged = [...state.notifications, ...unique].slice(0, MAX_NOTIFICATIONS);

      return { notifications: merged };
    });
  },

  /**
   * Marks a single notification as read by ID without removing it from the list.
   * @param {string|number|object} id - Notification ID or notification object.
   */
  markAsRead: id => {
    set(state => {
      const targetKey = toNotificationKey(id);

      if (targetKey === null) return state;

      const target = state.notifications.find(n => toNotificationKey(n) === targetKey);
      const wasUnread = Boolean(target) && !isNotificationRead(target);
      const notifications = state.notifications.map(n =>
        toNotificationKey(n) === targetKey ? markNotificationRead(n) : n
      );

      return {
        notifications,
        // Decrement rather than recount: the list holds one page, the count is global.
        unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
      };
    });
  },

  /**
   * Removes a notification from the list (e.g. after the read exit animation completes).
   * @param {string|number|object} id - Notification ID or notification object.
   */
  removeNotification: id => {
    set(state => {
      const targetKey = toNotificationKey(id);

      if (targetKey === null) return state;

      const target = state.notifications.find(n => toNotificationKey(n) === targetKey);

      if (!target) return state;

      const notifications = state.notifications.filter(n => toNotificationKey(n) !== targetKey);

      return {
        notifications,
        unreadCount: isNotificationRead(target) ? state.unreadCount : Math.max(0, state.unreadCount - 1),
      };
    });
  },

  markAllAsRead: () => {
    set(state => ({
      notifications: state.notifications.map(markNotificationRead),
      unreadCount: 0,
    }));
  },

  clearNotifications: () => set({ notifications: [], unreadCount: 0 }),
}));

export default useNotificationStore;
